import { AloftDate, expected } from '@aloft/toolkit-shared';
import { Modal } from '@aloft/toolkit-sui';

import _ from 'lodash';
import { useState } from 'react';

import { useAutofillProperty } from '../../../../hooks/useAutofillProperty.js';
import { ToastOptions } from '../../../../hooks/useToaster/types.js';
import { useToaster } from '../../../../hooks/useToaster/useToaster.js';
import { useZodForm } from '../../../../hooks/useZodForm.js';
import { addComps } from '../../../../redux/features/actions/addComps.js';
import {
  selectEffectiveDate,
  selectOrder,
} from '../../../../redux/features/selectors/orderSelectors.js';
import { selectImprovementsById } from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.js';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.js';
import { AddCompsForm } from './internal/AddCompsForm.js';
import { AddComparablesForm, schAddComparablesForm } from './types.js';
import { useUpdateOrderFeatures } from '../../../orderManagement/components/OrderTable/hooks/useUpdateOrderFeatures.js';

export function AddComparablesModal({
  onModalClose,
  isModalOpen,
}: {
  onModalClose: () => void;
  isModalOpen: boolean;
}) {
  const dispatch = useAppDispatch();
  const autofillProperty = useAutofillProperty();
  const updateOrderFeatures = useUpdateOrderFeatures();
  const effectiveDate = useAppSelector((state) => selectEffectiveDate(state));
  const orderID = useAppSelector(selectOrder).id;
  const subjectQuality = useAppSelector((state) =>
    selectImprovementsById(state, 'subject'),
  )?.craftsmanQuality;

  const [isLoading, setIsLoading] = useState(false);
  const toast = useToaster();

  const autofillForm = useZodForm({
    schema: schAddComparablesForm,
    defaultValues: {
      // Without these empty strings, we get a warning that an
      // uncontrolled input is changing to a controlled input (even though
      // we do have defaults in the zod schema, which is a separate thing)
      address: {
        AddressLineText: '',
        unitNumber: '',
        CityName: '',
        StateCode: '',
        PostalCode: '',
      },
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const submitAutofill = autofillForm.handleSubmit(async (submitParams: AddComparablesForm) => {
    setIsLoading(true);
    const successToastOptions: ToastOptions = {
      type: 'success' as const,
      id: 'add-comps-via-import',
      title: 'Successfully added property!',
    };

    const handleCompletion = (toastOptions: ToastOptions) => {
      toast(toastOptions);
      setIsLoading(false);
      if (toastOptions.type === 'success') {
        onModalClose();
      }
    };

    try {
      const property = await autofillProperty({
        address: expected(submitParams.address),
        effectiveYear: AloftDate.schema.parse(expected(effectiveDate)).getYear(),
        propertyType: submitParams.propertyType,
        orderID,
      });

      // Product ask: for manufactured and multifamily comps, override the quality
      // with what the subject's current quality is. SFRs will use the quality
      // determined throught the "regular" code path.
      if (
        submitParams.propertyType === 'manufacturedHome' ||
        submitParams.propertyType === 'multiFamily'
      ) {
        const overrideQualityPreset =
          subjectQuality ?? property.editableData.improvements.craftsmanQuality;
        _.set(property, 'editableData.improvements.craftsmanQuality', overrideQualityPreset);
      }

      dispatch(addComps([property]));
      updateOrderFeatures(orderID, [property]);
    } catch (e: unknown) {
      handleCompletion({
        type: 'error',
        id: 'add-comps-via-import-error',
        title: 'Failed to add property.',
      });
      return;
    }

    return handleCompletion(successToastOptions);
  });

  const autofillFields = autofillForm.watch();

  const isAutofillValid = () => {
    const address = autofillFields.address;
    const { propertyType } = autofillFields;

    return (
      address?.AddressLineText &&
      address?.CityName &&
      address?.StateCode &&
      address?.PostalCode &&
      propertyType
    );
  };

  return (
    <Modal
      size="lg"
      isOpen={isModalOpen}
      onClose={onModalClose}
      closeOnEsc
      title="Manually Add Comp"
      primaryAction={{
        id: 'add-btn',
        text: 'Save',
        onClick: (e) => submitAutofill(e),
        disabled: !isAutofillValid(),
        isLoading,
      }}
      secondaryAction={{
        id: 'cancel-btn',
        text: 'Cancel',
        onClick: () => {
          autofillForm.reset();
          onModalClose();
        },
      }}
    >
      <AddCompsForm form={autofillForm} />
      {autofillForm.formState.errors.address?.message && (
        <p>{autofillForm.formState.errors.address.message}</p>
      )}
    </Modal>
  );
}
